import React, { useEffect, useState } from "react";
import "./AssignMemberScreen.css";
import { getUserId } from "../../utils/tokenUtils";
import API_URL from "../../apiConfig";
import UserProfileBox from "../userProfileBox/UserProfileBox";
import { useNavigate } from "react-router-dom";
import { HelenService } from "../../services/helen/helenService";
import { TeamMembersService } from "../../services/teamMembers/teamMembersService";
import TopBar from "../../components/TopBar/TopBar";
import Tooltip from "../../components/Tooltip";

interface Bot {
  "User ID": number;
  "Login Email": string;
  "Helen Licenses Assigned": number;
  "Helen IDs Assigned": { id: string; assignedAt: string }[];
  "Full Name": string;
  pendingTasks: number;
}

interface TeamMember {
  user_id: number;
  first_name: string;
  last_name: string;
  login_email: string;
  role: string;
  status: string;
}

interface HelenIdInfo {
  helen_name: string;
  id: string;
  start_date: string;
  is_active: boolean;
  assigned_team_member_id?: number;
  time_limit: string;
  start_time: string;
  end_time: string;
  time_limit_applies: string;
  plan_name: string;
  usage_limit: string;
}

const AssignHelenScreen: React.FC = () => {
  const [helenIds, setHelenIds] = useState<HelenIdInfo[]>([]);
  const [bots, setBots] = useState<Bot[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [selectedHelen, setSelectedHelen] = useState<HelenIdInfo | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [tooltipTarget, setTooltipTarget] = useState<DOMRect | null>(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const loginemail = localStorage.getItem("Login Email");
  const navigate = useNavigate();
  const [createdDate, setCreatedDate] = useState<string | null>(null);
  const userId = getUserId();

 useEffect(() => {
    HelenService.getBotsAssignedToUser("active").then((resp) => {
      if (resp?.data?.userAssignedBots) {
        setHelenIds(resp.data.userAssignedBots);
      }
    });

    TeamMembersService.getTeamMembers(loginemail).then((resp) =>
      setTeamMembers(resp?.data?.teamMembers || [])
    );
  }, [selectedMember]);

  const handleMemberSelect = (
    helenIdInfo: HelenIdInfo,
    member: TeamMember | undefined
  ) => {
    if (member) {
      setSelectedMember(member);
      setSelectedHelen(helenIdInfo);
      setShowPopup(true);
    }
  };

  const handleConfirmAssign = () => {
    if (selectedMember && selectedHelen!== null) {
      HelenService.assignBotToTeamMember(
        userId,
        selectedMember.user_id,
        selectedHelen?.id
      ).then(() => {
        setBots((prev) => {
          const updated = prev.filter(
            (bot) =>
              !(bot["Helen IDs Assigned"] || []).some(
                (hid) => hid.id === selectedHelen?.id
              )
          );
          return [
            ...updated,
            {
              "User ID": selectedMember.user_id,
              "Login Email": selectedMember.login_email,
              "Helen Licenses Assigned": 1,
              "Helen IDs Assigned": [
                { id: selectedHelen?.id, assignedAt: new Date().toISOString() },
              ],
              "Full Name": `${selectedMember.first_name} ${selectedMember.last_name}`,
              pendingTasks: 0,
            },
          ];
        });
        setShowPopup(false);
        setSelectedMember(null);
        setSelectedHelen(null);
      });
    }
  };


  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="background-bots-section">
      <div className="dashboard-container">
        <TopBar />

        <div className="bots-section">
          <h2>Manage Bots</h2>
          <div className="table-wrapper">
            <div className="table-header">
              <div>BOT DETAILS</div>
              <div>PLAN NAME</div>
              <div>PURCHASED ON</div>
              <div>TASK QUEUE</div>
              <div>STATUS</div>
              <div>ASSIGNED MANAGER</div>
            </div>

            <div className="bots-grid">
              {helenIds.map((helen) => {

              const formatTooltipText = (bot: HelenIdInfo) => {
                  return `📊 Daily Interactions: ${bot.usage_limit}`;
                };

                return (
                  <div className="bot-card" key={helen.id}>
                    <div className="bot-cell">{helen.helen_name.toUpperCase()}</div>
                    <div className="bot-cell tooltip">{helen.plan_name} 
                      <button 
                        className="info-icon-btn"
                        onMouseEnter={(e) => {
                          setTooltipTarget(e.currentTarget.getBoundingClientRect());
                          setTooltipContent(`📊 Daily Interactions: ${helen.usage_limit}`);
                        }}
                        onMouseLeave={() => {
                          setTooltipTarget(null);
                          setTooltipContent("");
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                        </svg>
                      </button>
                    </div>
                    <div className="bot-cell">
                      {helen.start_date ? formatDate(helen.start_date) : "-"}
                    </div>
                    <div className="bot-cell">{0}</div>
                    <div className="bot-cell">
                      <span
                        className={`bot-status ${
                          helen.is_active ? "status-active" : "status-inactive"
                        }`}
                      >
                        {helen.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="bot-cell">
                      <select
                        className="assign-member-dropdown"
                        value={helen.assigned_team_member_id?.toString() || ""} 
                        onChange={(e) => {
                          const member = teamMembers.find(
                            (m) => m.user_id.toString() === e.target.value
                          );
                          handleMemberSelect(helen, member);
                        }}
                      >
                        <option value="">Assign Member</option>
                        {teamMembers.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.first_name} {member.last_name}
                            {helen.assigned_team_member_id?.toString() ===
                            member.user_id.toString()
                              ? " (Assigned)"
                              : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {showPopup && selectedMember && selectedHelen?.id !== null && (
            <div className="assign-popup" onClick={() => setShowPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="assign-close-btn" onClick={() => setShowPopup(false)} aria-label="Close">×</button>

                <div className="assign-modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>

                <p className="assign-modal-title">Assign Helen</p>

                <p className="assign-heading">
                  {bots.some(
                    (bot) =>
                      bot["Helen IDs Assigned"].some(
                        (hid) => hid.id === selectedHelen?.id
                      ) && bot["User ID"] !== selectedMember.user_id
                  )
                    ? `Are you sure you want to reassign ${selectedHelen?.helen_name.toUpperCase()}?`
                    : `Are you sure you want to assign ${selectedHelen?.helen_name.toUpperCase()}?`}
                </p>

                <div className="profile-section">
                  <div className="avatar-initial">
                    {`${selectedMember.first_name?.[0] || ""}${selectedMember.last_name?.[0] || ""}`}
                  </div>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#36363C" }}>
                    {selectedMember.first_name} {selectedMember.last_name}
                  </span>
                </div>

                <div className="popup-actions">
                  <button className="assign-cancel-button" onClick={() => setShowPopup(false)}>Cancel</button>
                  <button className="assign-confirm-button" onClick={handleConfirmAssign}>Confirm</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <Tooltip targetRect={tooltipTarget} content={tooltipContent} />
      </div>
    </div>
  );
};

export default AssignHelenScreen;
